import axios from "axios";

import type {
	Application,
	BlockMessage,
	CompletedStep,
	Message,
	MessageWithContents,
	Session,
	Template,
	TemplateStep,
	UserApplication,
} from "@virtual-brain-lib/types";

import env from "../environments";
import { queryOptions } from "../query-client";

export async function startApplication({ id, steps }: { id: string; steps: CompletedStep[] }) {
	const response = await axios.post<{ messages: Message[]; session: Session }>(
		`${env.apiUrl}/applications/${id}`,
		{
			steps,
		},
	);

	return response.data;
}

export const getAllApplications = queryOptions({
	async queryFn() {
		const response = await axios.get<Application[]>(`${env.apiUrl}/applications`);

		return response.data;
	},
	queryKey: "applications",
});

export async function createApplication() {
	const response = await axios.post<{ application: Application }>(
		`${env.apiUrl}/applications/create`,
	);

	return response.data.application;
}

export async function createTemplate({
	applicationId,
	instructions,
}: {
	applicationId: string;
	instructions: Array<Partial<TemplateStep>>;
}) {
	const response = await axios.post<{ application: Application }>(
		`${env.apiUrl}/applications/${applicationId}/template`,
		{
			instructions,
		},
	);

	return response.data.application;
}

export async function createApplicationFromTemplate({
	templateId,
	selectedBrains,
}: {
	templateId: string;
	selectedBrains: Array<{ stepId: string; brainId: string }>;
}) {
	const response = await axios.post<{ application: Application }>(
		`${env.apiUrl}/applications/${templateId}/createFromTemplate`,
		{ selectedBrains },
	);

	return response.data.application;
}

export async function duplicateApplication(id: string) {
	const response = await axios.post<{ application: Application }>(
		`${env.apiUrl}/applications/${id}/duplicate`,
	);

	return response.data.application;
}

export async function sendFollowUpQuestion({
	blockId,
	sessionId,
	followup,
	artifactId,
}: {
	blockId: string;
	sessionId: string;
	followup: string;
	artifactId: string;
}) {
	const endpoint = `${env.apiUrl}/applications/session/${blockId}/followup`;

	const payload = {
		followup,
		sessionId,
		artifactId,
	};

	const response = await axios.post<{
		userMessage: Message;
		assistantMessage: Message;
		session: Session;
	}>(endpoint, payload);

	return response.data;
}

export async function retryBlock({
	blockId,
	sessionId,
	applicationId,
}: {
	blockId: string;
	sessionId: string;
	applicationId: string;
}) {
	const response = await axios.post<BlockMessage>(
		`${env.apiUrl}/applications/${applicationId}/session/${sessionId}/blocks/${blockId}/retry`,
	);

	return response.data;
}

export async function continueBlock({
	sessionId,
	blockId,
	applicationId,
	currentBlockMessageId,
}: {
	sessionId: string;
	blockId: string;
	applicationId: string;
	currentBlockMessageId: string;
}) {
	const response = await axios.post<{ currentBlockMessageId: string }>(
		`${env.apiUrl}/applications/${applicationId}/session/${sessionId}/blocks/${blockId}/continue`,
		{ currentBlockMessageId },
	);

	return response.data;
}
/* -------------------------------------------------- UPDATES -------------------------------------------------- */

export async function updateApplication(
	application: Partial<Application> & Pick<Application, "id">,
) {
	await axios.put(`${env.apiUrl}/applications/${application.id}`, { application });
}

export async function updateSessionTitle({
	sessionId,
	title,
}: {
	sessionId: string;
	title: string;
}) {
	await axios.put(`${env.apiUrl}/applications/session/${sessionId}`, { title });
}

export async function updateTemplate({
	applicationId,
	instructions,
}: {
	applicationId: string;
	instructions: Array<Partial<TemplateStep>>;
}) {
	await axios.put(`${env.apiUrl}/applications/${applicationId}/template`, { instructions });
}

/* -------------------------------------------------- GETS -------------------------------------------------- */

export async function getSessionBlockMessagesFn(sessionId: string, blockId: string) {
	const response = await axios.get<MessageWithContents[]>(
		`${env.apiUrl}/applications/session/${sessionId}/${blockId}`,
	);

	return response.data;
}

export const getBlockChatMessage = queryOptions({
	async queryFn({ queryKey }) {
		const [, { messageId }] = queryKey;

		const response = await axios.get<MessageWithContents>(
			`${env.apiUrl}/applications/session/messages/${messageId}`,
		);

		return response.data;
	},
	queryKey: "blockChatMessage",
});

export const getApplicationSessionMessages = queryOptions({
	async queryFn({ queryKey }) {
		const [, { sessionId, blockIds }] = queryKey;

		const messages = await Promise.all(
			blockIds.map(async (blockId) => getSessionBlockMessagesFn(sessionId, blockId)),
		);

		return messages.flat().sort((a, b) => {
			const aTime = a.message.createdAt ? new Date(a.message.createdAt).getTime() : 0;
			const bTime = b.message.createdAt ? new Date(b.message.createdAt).getTime() : 0;

			return aTime - bTime;
		});
	},
	queryKey: "applicationSessionMessages",
});

export const getSession = queryOptions({
	async queryFn({ queryKey }) {
		const [, { sessionId }] = queryKey;

		const response = await axios.get<{ session: Session }>(
			`${env.apiUrl}/applications/sessions/${sessionId}`,
		);

		return response.data.session;
	},
	queryKey: "applicationSession",
});

export async function getApplicationDataFn(id: string) {
	const response = await axios.get<Application>(`${env.apiUrl}/applications/${id}`);

	return response.data;
}

export const getApplicationData = queryOptions({
	async queryFn({ queryKey }) {
		const [, { applicationId }] = queryKey;

		return getApplicationDataFn(applicationId);
	},
	queryKey: "applicationData",
});

export async function getApplicationUsers(id: string) {
	const response = await axios.get<UserApplication[]>(
		`${env.apiUrl}/applications/sharedWith/${id}`,
	);

	return response.data;
}

export const getTemplateSteps = queryOptions({
	async queryFn({ queryKey }) {
		const [, { templateId }] = queryKey;
		const response = await axios.get<{ templateSteps: TemplateStep[] }>(
			`${env.apiUrl}/applications/${templateId}/templateSteps`,
		);

		return response.data.templateSteps;
	},
	queryKey: "templateSteps",
});

export const getTemplates = queryOptions({
	async queryFn() {
		const response = await axios.get<{ templateList: Template[] }>(
			`${env.apiUrl}/applications/templates/all`,
		);

		return response.data.templateList;
	},
	queryKey: "applicationTemplates",
});

/* -------------------------------------------------- DELETES -------------------------------------------------- */

export async function deleteApplication(id: string) {
	await axios.delete(`${env.apiUrl}/applications/${id}`);
}

export async function deleteSession(sessionId: string) {
	await axios.delete(`${env.apiUrl}/applications/session/${sessionId}`);
}
